import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseDialog } from "./dialog";
import { Button } from "@/components/ui/button";
import { createClientDocuments, getContracts } from "@/service/weexpertService";
import { toast } from "../ui/use-toast";
import { SelectWithAlert } from "../ui/select-with-alert";
import {
  CLIENT_ARTIFACT_TYPES,
  LEGITYMACIA,
  OSWIADCZENIE,
  ZASWIADCZENIE_Z_DZIEKANATY,
  PASSPORT,
  PESEL_UKR,
  VISA,
  VISA_OPTIONS,
  ZEZWOLENIE_TYPY_A,
  DECYZJA_DO_KARTY_POBYTU_ARTYKULY,
  KARTA_POBYTU,
  DECYZJA_DO_KARTY_POBYTU,
  ZGLOSZENIE_UKR,
  QUESTIONARY,
  NIEPODJECIE,
  PODJECIE,
  DOSTEP_DO_RYNKU_PRACY,
  STAMP,
  validateNoneCyrillic,
} from "@/constants";
import { useQueryClient } from "react-query";
import { DatePicker } from "../ui/date-picker";
import { InputWithAlert } from "../ui/input-with-alert";
import { format, isAfter, isSameDay } from "date-fns";
import { InfoIcon, AlertCircle } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { Alert, AlertTitle } from "../ui/alert";
import { MetatdataInput } from "../documents/metadata.form";
import FileUploader from "../global/FileUploader";

const convertDates = (obj) => {
  if (Array.isArray(obj)) return obj.map(convertDates);

  if (obj && typeof obj === "object")
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value instanceof Date
          ? format(value, "yyyy-MM-dd")
          : convertDates(value),
      ])
    );

  return obj;
};

function doesTypeHaveValidPeriod(type, metadata, key) {
  const passportSchema = {
    validPeriod: true,
    documentId: true,
  };

  const questionary = {};

  const visaSchema = {
    validPeriod: true,
    documentId: true,
    metadata: {
      visaType: {
        type: "select",
        options: VISA_OPTIONS,
      },
    },
  };
  const zezwolenie_typ_a = {
    validPeriod: true,
    documentId: true,
    metadata: {
      zezwolenie_type: {
        type: "select",
        options: [
          {
            value: "UD",
            label: "UD",
          },
          {
            value: "UZ",
            label: "UZ",
          },
        ],
        required: true,
      },
    },
  };

  const oswiadczenie = {
    validPeriod: {
      hint: `OŚWIADCZENIE
PODMIOTU NIEŚWIADCZĄCEGO USŁUG
W ZAKRESIE PRACY TYMCZASOWEJ
O POWIERZENIU WYKONYWANIA PRACY CUDZOZIEMCOWI Punkt 3.1`
    },
    documentId: true,
    metadata: {
      oswiadczenie_type: {
        type: "select",
        options: [
          {
            value: "UD",
            label: "UD",
          },
          {
            value: "UZ",
            label: "UZ",
          },
        ],
        required: true,
      },
      minimum_amount: {
        type: "input",
        label: "minimalna kwota brutto",
      },
      tytul: {
        type: "text",
        label: "Stanowisko / rodzaj pracy wykonywanej przez cudzoziemca",
        hint: `OŚWIADCZENIE
PODMIOTU NIEŚWIADCZĄCEGO USŁUG
W ZAKRESIE PRACY TYMCZASOWEJ
O POWIERZENIU WYKONYWANIA PRACY CUDZOZIEMCOWI Punkt 3.1`,
      },
    },
  };

  const peselUkr = {
    validPeriod: false,
    documentId: false,
    metadata: {
      issued: {
        type: "date",
      },
    },
  };

  const zgloszenieUkr = {
    validPeriod: true,
    metadata: {
      zgloszenie_type: {
        type: "select",
        options: [
          {
            value: "UD",
            label: "UD",
          },
          {
            value: "UZ",
            label: "UZ",
          },
        ],
        required: true,
      },
    },
  };

  const kartaPobytu = {
    validPeriod: true,
    documentId: true,
  };

  const niepodjecie = {
    documentId: true,
  };

  const podjecie = {
    documentId: true,
  };

  const decyzjaDoKartyPobytu = {
    validPeriod: true,
    metadata: {
      art: {
        type: "select",
        options: DECYZJA_DO_KARTY_POBYTU_ARTYKULY,
      },
    },
  };

  const legitymacia = {
    validPeriod: true,
    documentId: false,
  };

  const zaswiadczenie_z_dziekanatut = {
    validPeriod: true,
    documentId: false,
  };

  const dostep_do_rynku_pracy = {
    validPeriod: true,
    documentId: false,
  };
  const stamp = {
    validPeriod: true,
    documentId: false,
  };
  // special case for visa free
  if (
    type == VISA &&
    key === "documentId" &&
    metadata &&
    metadata.visaType &&
    metadata.visaType.value == "visa_free"
  ) {
    return false;
  }
  switch (type) {
    case PASSPORT:
      return passportSchema[key] ?? false;
    case VISA:
      return visaSchema[key] ?? false;
    case STAMP:
         return stamp[key] ?? false;
    case ZEZWOLENIE_TYPY_A:
      return zezwolenie_typ_a[key] ?? false;
    case OSWIADCZENIE:
      return oswiadczenie[key] ?? false;
    case ZASWIADCZENIE_Z_DZIEKANATY:
      return zaswiadczenie_z_dziekanatut[key] ?? false;
    case LEGITYMACIA:
      return legitymacia[key] ?? false;
    case PESEL_UKR:
      return peselUkr[key] ?? false;
    case KARTA_POBYTU:
      return kartaPobytu[key] ?? false;
    case DECYZJA_DO_KARTY_POBYTU:
      return decyzjaDoKartyPobytu[key] ?? false;
    case ZGLOSZENIE_UKR:
      return zgloszenieUkr[key] ?? false;
    case QUESTIONARY:
      return questionary[key] ?? false;
    case NIEPODJECIE:
      return niepodjecie[key] ?? false;
    case PODJECIE:
      return podjecie[key] ?? false;
    case DOSTEP_DO_RYNKU_PRACY:
      return dostep_do_rynku_pracy[key] ?? false;
    default:
      return false; //throw new Error("Type is now known to system")
  }
}

const getDocumentType = (documents, t = null) => {
  const resultDocuments = [PASSPORT, QUESTIONARY, DOSTEP_DO_RYNKU_PRACY];
  if (documents.filter((e) => [PASSPORT].includes(e.entityType)).length > 0) {
    resultDocuments.push(
      ...[
        VISA,
        PESEL_UKR,
        DECYZJA_DO_KARTY_POBYTU,
        ZGLOSZENIE_UKR,
        STAMP
      ]
    );
  }

  if (
    documents.filter((e) =>
      [VISA, DECYZJA_DO_KARTY_POBYTU, STAMP].includes(e.entityType)
    ).length > 0
  ) {
    resultDocuments.push(...[ZEZWOLENIE_TYPY_A, OSWIADCZENIE, LEGITYMACIA]);
  }

  if (
    documents.filter((e) => [DECYZJA_DO_KARTY_POBYTU].includes(e.entityType))
      .length > 0
  ) {
    resultDocuments.push(...[KARTA_POBYTU]);
  }

  if (
    documents.filter((e) => [LEGITYMACIA].includes(e.entityType)).length > 0
  ) {
    resultDocuments.push(...[ZASWIADCZENIE_Z_DZIEKANATY]);
  }

  return CLIENT_ARTIFACT_TYPES(t).filter((e) =>
    resultDocuments.includes(e.value)
  );
};

export default function ClientDocumentUpload(props) {
  const { id, documents } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState();
  const [files, setFiles] = useState([]);
  const queryClient = useQueryClient();
  // valid from
  const [validFrom, setValidFrom] = useState(new Date());
  const [validTo, setValidTo] = useState(new Date());
  const [documentId, setDocumnetId] = useState();
  const [metadata, setMetadata] = useState({});
  const [inputChecked, setInputChecked] = useState(false);
  const [isButtonBlocked, setIsButtonBlocked] = useState(false);
  const docsOptions = getDocumentType(documents);
  const handleUpload = () => {
    setInputChecked(true);
    if (!selectedType) {
      return;
    }
    if (!files || files.length === 0) {
      toast({
        title: "Please upload a file before submitting.",
        variant: "destructive",
      });
      return;
    }
    // todo validation for each type

    const haveValidPeriod = doesTypeHaveValidPeriod(
      selectedType,
      metadata,
      "validPeriod",
    );
    const haveUniqueueId = doesTypeHaveValidPeriod(
      selectedType,
      metadata,
      "documentId"
    );

    if (
      haveValidPeriod &&
      (!validTo || !validTo || isAfter(validFrom, validTo) || isSameDay(validFrom, validTo))
    ) {
      return;
    }

    if (haveUniqueueId && (!documentId || !validateNoneCyrillic(documentId))) {
      return;
    }
    setIsButtonBlocked(true);

    toast({
      title: "Starting uploading",
    });
    return createClientDocuments(
      files,
      id,
      haveUniqueueId ? documentId : null,
      selectedType,
      haveValidPeriod ? format(validFrom, "yyyy-MM-dd") : null,
      haveValidPeriod ? format(validTo, "yyyy-MM-dd") : null,
      metadata ? convertDates(metadata) : {}
    )
      .then((res) => {
        toast({
          title: "Success",
        });
      })
      .catch((err) => {
        toast({
          title: "Error",
          variant: "destructive",
        });
      })
      .finally(() => {
        queryClient.invalidateQueries(["client_documents"]);
        setOpen(false);
        setFiles([]);
        setValidFrom(new Date());
        setValidTo(new Date());
        setDocumnetId();
        setMetadata({});
        setSelectedType();
        setIsButtonBlocked(false);
      });
  };

  return (
    <BaseDialog
      dialogTitle={""}
      buttonText={t("common:button_upload")}
      open={open}
      setOpen={setOpen}
    >
      <div className="w-[400px] p-1">
        <div className="py-2">
          <SelectWithAlert
            inputClassName={"h-10 mt-2 bg-white"}
            label={t("common:select_type_of_document")}
            placeholder={t("common:select_type_of_document")}
            value={selectedType}
            onChangeValue={(e) => setSelectedType(e)}
            options={docsOptions}
            error={inputChecked && !selectedType}
            errorMessage={"Please select a document type"}
            inputConent={'h-[200px]'}
          />
        </div>
        {doesTypeHaveValidPeriod(selectedType, metadata, "metadata") && (
          <div className="pt-4">
            <MetatdataInput
              metadataSchema={doesTypeHaveValidPeriod(
                selectedType,
                metadata,
                "metadata"
              )}
              onChange={(e) => setMetadata(e)}
            />
          </div>
        )}
        {doesTypeHaveValidPeriod(selectedType, metadata, "validPeriod") && (
          <div>
            <div className="flex justify-between pt-2">
              <div className="w-[48%]">
                <DatePicker
                  date={validFrom}
                  defaultMonth={validFrom}
                  setDate={(e) => setValidFrom(e)}
                  error={!validFrom || isAfter(validFrom, validTo)}
                  errorMessage={t("empty_data_invalid")}
                  label={t("valid_from")}
                />
              </div>
              <div className="w-[48%]">
                <DatePicker
                  date={validTo}
                  defaultMonth={validTo}
                  disabled={{ before: validFrom }}
                  setDate={(e) => setValidTo(e)}
                  error={!validTo || isAfter(validFrom, validTo)}
                  errorMessage={t("empty_data_invalid")}
                  label={
                    <>
                      <div className="flex items-center">
                        {t("valid_to")}
                        {doesTypeHaveValidPeriod(
                          selectedType,
                          metadata,
                          "validPeriod"
                        ).hint && (
                            <HoverCard>
                              <HoverCardTrigger>
                                <InfoIcon className="h-5" />
                              </HoverCardTrigger>
                              <HoverCardContent>
                                <span>
                                  {
                                    doesTypeHaveValidPeriod(
                                      selectedType,
                                      metadata,
                                      "validPeriod"
                                    ).hint
                                  }
                                </span>
                              </HoverCardContent>
                            </HoverCard>
                          )}
                      </div>
                    </>
                  }
                />
              </div>
            </div>
            {validFrom && validTo && isSameDay(validFrom, validTo) && (
              <div className="mt-4">
                <Alert variant="destructive" className="relative mb-2 h-12 flex items-center justify-start !pl-[0.75rem]">
                  <AlertCircle className="h-4 w-4 !static !mr-2" />
                  <AlertTitle className="m-0 !p-0">
                    {t("same_day_dates_invalid")}
                  </AlertTitle>
                </Alert>
              </div>
            )}
            <div></div>
          </div>
        )}
        {doesTypeHaveValidPeriod(selectedType, metadata, "documentId") && (
          <div className="w-full pt-4">
            <div>
              <InputWithAlert
                label={t("document_id")}
                value={documentId}
                onChange={(e) => {
                  setDocumnetId(e.target.value);
                }}
                inputClassName={"h-10"}
                error={
                  inputChecked &&
                  ((doesTypeHaveValidPeriod(
                    selectedType,
                    metadata,
                    "documentId"
                  ) &&
                    !documentId) ||
                    (documentId && !validateNoneCyrillic(documentId)))
                }
                errorMessage={
                  documentId && !validateNoneCyrillic(documentId)
                    ? t("cyrillic_not_allowed")
                    : t("empty_data_invalid")
                }
              />
            </div>
          </div>
        )}
        <div className="pt-4">
          <FileUploader onChange={setFiles} />
        </div>
      </div>
      <div className="w-full flex justify-end pt-4">
        <Button disabled={isButtonBlocked} onClick={handleUpload}>
          {t("common:button_upload")}
        </Button>
      </div>
    </BaseDialog>
  );
}
